/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { BaseComponent } from '../../../../../../core/components';
import { Component } from '@angular/core';
import { DropdownModel, ErrorResponseModel } from '../../../../../../core/models';
import { LogService, NotifyService, TransactionService, UtilsService } from '../../../../../../core/services';
import { NetworkingApiService, NetworkingService, NetworkInterfaceModel } from '../../../../shared';
import { concatMap, takeUntil, toArray } from 'rxjs/operators';
import { from, Observable } from 'rxjs';
import { ipValidator, nameValidation } from '../../../../../../core/directives';

@Component({
    selector: 'app-add-bond',
    templateUrl: './add-bond.component.html',
    styleUrls: ['./add-bond.component.scss']
})
export class AddBondComponent extends BaseComponent {
    /**
     * FormGroup.
     */
    public formGroup: FormGroup;

    /**
     * Store the interfaces that can be bonded
     */
    public bondableInterfaces: NetworkInterfaceModel[] = [];

    /**
     * Store for dropdown content.
     */
    public dropdown: DropdownModel[] = [
        <DropdownModel>{ key: 'balance-rr', value: 'balance-rr' },
        <DropdownModel>{ key: 'active-backup', value: 'active-backup' },
        <DropdownModel>{ key: '802.3ad', value: '802.3ad' },
    ];

    /**
     * Store the interfaces for checks against label.
     * @private
     */
    private storedInterfaces: NetworkInterfaceModel[] = [];

    /**
     * Store the ip validator for the view.
     * @protected
     */
    protected readonly ipValidator = ipValidator;

    constructor(
        private activatedRoute: ActivatedRoute,
        private formBuilder: FormBuilder,
        private logService: LogService,
        private networkingApiService: NetworkingApiService,
        private networkingService: NetworkingService,
        private notifyService: NotifyService,
        private router: Router,
        private transactionService: TransactionService,
        private utilsService: UtilsService
    ) {
        super();

        this.setUpPageData();
        this.setupFormGroups();
        this.setUpFormSubscribers();
    }

    get getInterfacesFields() {
        return this.formGroup.get('interfaces') as FormArray;
    }

    /**
     * Convert control to type FormControl
     * @param control
     */
    public getFormControl(control: any): FormControl {
        return control as FormControl;
    }

    /**
     * Add bond via the API.
     * @private
     */
    public addBond(): Observable<any> {
        return new Observable<any>(observer => {
            this.notifyService.muteLocalNotifications();

            const formValues = this.formGroup.value;
            const { dnsIPv4, dnsIPv6 } = this.networkingService.sortDNS(formValues.dns);
            formValues.dnsIPv4 = dnsIPv4;
            formValues.dnsIPv6 = dnsIPv6;
            delete formValues.dns;

            this.transactionService.startTransaction()
                .pipe(
                    concatMap(() => this.networkingApiService.addBond(formValues)),
                    concatMap(newBond => this.addInterfaceToBond(newBond.id)),
                    concatMap(() => this.networkingService.commitTransaction()),
                    takeUntil((this.destroyed$)))
                .subscribe({
                    next: (): void => {
                        this.notifyService.unmuteLocalNotifications();
                        this.notifyService.success('Bond created');
                        this.router.navigate(['../'], { relativeTo: this.activatedRoute }).then().catch();
                        observer.next();
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel) => {
                        this.logService.error('AddBondComponent: addBond() error adding bond ', error.message);
                        this.networkingService.submitFailed();
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Add interface to bond.
     * @param bondId
     * @private
     */
    private addInterfaceToBond(bondId: string): Observable<any> {
        return new Observable<any>(observer => {
            const interfaceIds = this.getInterfaceIds();
            if (!interfaceIds.length) {
                observer.next(false);
                observer.complete();
            }

            const bond = { type: 'bondChild', parentId: bondId };

            from(interfaceIds)
                .pipe(concatMap(interfaceId => this.networkingApiService.addInterfaceToBond(bond, interfaceId)),
                    toArray(),
                    takeUntil(this.destroyed$)
                )
                .subscribe({
                    next: (response: any): void => {
                        observer.next(response);
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.logService.error('AddBondComponent:addInterfaceToBond() error', error.message);
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Get interfaceIds from bondable Interfaces.
     * @private
     */
    private getInterfaceIds(): string[] {
        const interfaceIds = [];
        const interfaces = this.formGroup.get('interfaces').value;

        if (interfaces.length !== this.bondableInterfaces.length) {
            this.logService.error('getInterfaceIds', 'There is a length mismatch on the interface arrays');
        }

        for (let i = 0; i < interfaces.length; i++) {
            const interfaceKey = interfaces[i];
            if (interfaceKey) {
                interfaceIds.push(this.bondableInterfaces[i].id);
            }
        }

        return interfaceIds;
    }

    /**
     * Set up page data via the API.
     * @private
     */
    private setUpPageData(): void {
        this.networkingApiService.getInterfaces()
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (interfaces: NetworkInterfaceModel[]): void => {
                    this.storedInterfaces = interfaces;
                    this.createInterfacesFormControls(interfaces);
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error(
                        'AddBondComponent:setUpPageData', 'Unable to get page data' + error.message);
                }
            });
    }

    /**
     * Setup Floating IP form.
     * @private
     */
    private setupFormGroups(): void {
        this.formGroup = this.formBuilder.group({
            name: [null, [Validators.required, nameValidation]],
            type: 'bond',
            interfaces: new FormArray([]),
            mtu: [1500],
            onBoot: [false],
            offload: [false],
            mode: ['balance-rr'],
            dns: new FormArray([new FormControl('', null)]),
        });
    }

    /**
     * Set up form subscribers.
     * @private
     */
    private setUpFormSubscribers(): void {
        const formGroupName: AbstractControl = this.formGroup.get('name');
        formGroupName.valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (name: any): void => {
                    this.utilsService.debounce('check-name', () => {
                        this.utilsService.checkDuplicateFormValue(
                            formGroupName, 'name', this.storedInterfaces, name, null
                        );
                    });
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('AddBondComponent:setUpFormSubscribers() error', error.message);
                }
            });
    }

    /**
     * Creates a form controller for each path in the template in edit mode
     * @param interfaces
     * @private
     */
    private createInterfacesFormControls(interfaces: NetworkInterfaceModel[]): void {
        this.bondableInterfaces = interfaces.filter((item: NetworkInterfaceModel): boolean => item.type === 'ethernet');
        for (let i = 0; i < this.bondableInterfaces.length; i++) {
            this.getInterfacesFields.push(new FormControl(null));
        }
    }
}
